import React, { useState, useEffect } from 'react';
import { History, Search, Calendar, User, LogIn } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginHistory() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState(7);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [userTypeFilter, daysFilter]);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/user-management/login-history?days=${daysFilter}`;
      if (userTypeFilter) url += `&user_type=${userTypeFilter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      } else {
        toast.error('Failed to fetch login history');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (log.full_name || '').toLowerCase().includes(searchLower) ||
      (log.username || '').toLowerCase().includes(searchLower)
    );
  });

  // Group logs by date
  const groupedLogs = {};
  filteredLogs.forEach(log => {
    const date = log.login_at ? log.login_at.split('T')[0] : 'Unknown';
    if (!groupedLogs[date]) groupedLogs[date] = [];
    groupedLogs[date].push(log);
  });

  const formatTime = (datetime) => {
    if (!datetime) return 'N/A';
    try {
      return new Date(datetime).toLocaleTimeString('bn-BD', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  // Bug fix: removed shadowing formatDate – using imported one

  return (
    <div className="p-6 space-y-6" data-testid="login-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Login History</h1>
          <p className="text-gray-500">Track user login activity</p>
        </div>
        <Button onClick={fetchLogs} variant="outline">
          <History className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-center gap-4">
          <LogIn className="h-10 w-10 text-blue-600" />
          <div>
            <p className="text-2xl font-bold text-blue-700">{logs.length}</p>
            <p className="text-sm text-blue-600">Total Logins (Last {daysFilter} days)</p>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or username..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>
            </div>
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Users</option>
              <option value="staff">Staff Only</option>
              <option value="student">Student Only</option>
            </select>
            <select
              value={daysFilter}
              onChange={(e) => setDaysFilter(parseInt(e.target.value))}
              className="px-4 py-2 border rounded-lg"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Login Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Login Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : Object.keys(groupedLogs).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No login activity found</p>
              <p className="text-sm">Users will appear here after they login</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedLogs).sort((a, b) => b[0].localeCompare(a[0])).map(([date, dateLogs]) => (
                <div key={date}>
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <h3 className="font-medium text-gray-700">{formatDate(date)}</h3>
                    <span className="text-sm text-gray-400">({dateLogs.length} logins)</span>
                  </div>
                  
                  <div className="space-y-2 ml-6">
                    {dateLogs.map((log, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            log.user_type === 'staff' ? 'bg-green-100' : 'bg-purple-100'
                          }`}>
                            <User className={`h-5 w-5 ${
                              log.user_type === 'staff' ? 'text-green-600' : 'text-purple-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{log.full_name || 'N/A'}</p>
                            <p className="text-sm text-gray-500">{log.username} • {log.role}</p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            log.user_type === 'staff' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {log.user_type === 'staff' ? 'Staff' : 'Student'}
                          </span>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatTime(log.login_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
